// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2024-05-29 18:16:54 +0200 (Wed, 29 May 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 143188 $
//  Purpose   : vltype rule
// =============================================================================
// ---Third-party imports
import React from 'react'

// ---Framework imports
import {mapItems, flatten, isNil, dict, range} from 'ldash'
import {Actions} from 'uix/registries'
import log from 'uix/logging'
import {getType} from 'uix/types'
import {CUSTOM_TREE_ICONS} from 'uix/types/utils'

// ---Local imports
import {NTYPE} from './const'
import {ID} from '../utils'

// ---Globals
// vl-type children of these datatypes will be alphabetically sorted
const DATATYPES_ALPHA_SORT = [13, 16, 19, 7]
const img = (imgSet, name, cname) => <img key={name} className={cname} src={imgSet[name]} />

export const VLTYPE = ({
  i18n,
  domains,
  loadedDomains,
  treeById,
  treeDetailsById,
  treeChildData,
  buildVlType,
  _buildVlType,
  Graphics,
  wasExpanded, // transport is-collapsed info
}) => ({
  // asd

  buildSingleNode: otid => (treeById[otid] ? buildVlType({otid, idContext: 'single'}) : null),
  buildNodes: () => {
    let language = i18n?._language || 'de'
    let i18nCache = {}
    let _i18n = (odid, datatype, subtype) => {
      let k = `${language}/${odid}/${datatype}/${subtype}`
      let result = i18nCache[k]
      if (!result) {
        //~ let di18n = ddata.i18n[i18n._language]
        try {
          result = i18n.getType(datatype, subtype)
        } catch (e) {
          result = `${datatype}/${subtype} [inactive]`
        }
        i18nCache[k] = result
      }
      return result
    }
    // deliverer_year nodes need fast access on dt14 nodes
    // probably also capsule nodes on metadata/dt1 nodes
    let nodesbyDatatype = dict(range(25).map(dt => [dt, []]))
    let nodes = flatten(
      flatten(
        mapItems(treeChildData, (idContext, contextData) =>
          mapItems(
            contextData,
            (parentId, otids) => {
              let children = otids.map(otid => {
                let child = treeById[otid]
                // XXX: is this really faster than buildVlType?
                let res = _buildVlType(
                  child,
                  treeDetailsById,
                  treeChildData,
                  wasExpanded,
                  idContext,
                  parentId,
                  _i18n
                )
                if (!child.slimlink) {
                  nodesbyDatatype[child.datatype].push(res)
                }
                return res
              })
              // XXX: wiki + resources need folder/file rules
              // datatype isNil for parentId == 0
              let datatype = treeById[parentId]?.datatype
              // apply alpha sorting for some datatypes
              if (isNil(datatype) || DATATYPES_ALPHA_SORT.includes(datatype)) {
                children.sort((e1, e2) => {
                  if (!e1.slimlink && !e2.slimlink) {
                    return e1.title.localeCompare(e2.title)
                  } else {
                    // slimlinked pages to the end
                    return 1
                  }
                })
              }
              return children
            },
            // filter children of collapsed nodes
            parentId => wasExpanded[ID(idContext, parentId)] !== false
          )
        )
      )
    )
    return {nodes, nodesbyDatatype}
  },

  getChildren: node =>
    Actions.getChildren({otid: node.otid, odid: node.odid, idContext: node.idContext}),

  icon: ({node, colorizeDomains, builder}) => {
    let data_id = node.slimlink || node.otid
    let data = treeById[data_id]

    if (!builder) {
      builder = img
    }

    let dname = !node.odlink ? domains.odid2name[node.odid] : domains.odid2name[node.odlink]
    if (!loadedDomains[dname]) {
      log.warn('%o not loaded', dname)
      return null
    }

    try {
      var type = getType(loadedDomains[dname].types, data.datatype, data.subtype)
    } catch (e) {
      log.error('inactive type: %o/%o [dname: %o]', data.datatype, data.subtype, dname)
      return null
    }
    let typeName = type.name.replace(/ /g, '_')

    let res = null
    // some container types have custom icons
    let customTreeIcon = CUSTOM_TREE_ICONS[typeName]
    if (customTreeIcon) {
      res = builder(Graphics.tree, customTreeIcon, 'treeIcon')
    } else {
      let n = `${type.datatypeName}/${typeName}`
      res = builder(Graphics.types, n, 'treeIcon')
    }

    let overlays = []

    if (data.otlink || data.slimlink) {
      overlays.push(builder(Graphics.common, 'link', 'overlayLink'))
    }

    if (data.delstate) {
      overlays.push(builder(Graphics.common, 'deleted', 'overlayDel'))
    }
    if (overlays.length > 0) {
      res = (
        <>
          {res}
          {overlays}
        </>
      )
    }
    return <span className="treeIconWrap">{res}</span>
  },
})
